import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Calendar, CreditCard, BarChart3, Shield, Smartphone } from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import './Pages.css';

const FeaturesPage: React.FC = () => {
  const features = [
    {
      icon: <Users size={48} />,
      title: 'Member Management',
      description: 'Complete member profiles, membership tracking, and automated renewals with our gym membership software. Manage hundreds of members effortlessly with our intuitive interface.',
      details: [
        'Complete member profiles with photos and documents',
        'Automated membership renewals and reminders',
        'Member history and activity tracking',
        'Bulk member operations',
        'Member search and filtering'
      ]
    },
    {
      icon: <Calendar size={48} />,
      title: 'Attendance System',
      description: 'Advanced attendance tracking system for gym with manual check-in and real-time monitoring. Know who\'s in your gym at any moment.',
      details: [
        'Manual check-in system',
        'Real-time attendance dashboard',
        'Attendance reports and analytics',
        'Guest pass management',
        'QR code check-in (Coming Soon)',
        'Biometric attendance (Coming Soon)'
      ]
    },
    {
      icon: <CreditCard size={48} />,
      title: 'Payment Processing',
      description: 'Seamless payment collection, automated invoicing, and financial reporting for your fitness studio. Get paid on time, every time.',
      details: [
        'Multiple payment methods support',
        'Automated payment reminders',
        'Payment history tracking',
        'Financial reports and analytics',
        'Invoice generation'
      ],
      comingSoon: true
    },
    {
      icon: <BarChart3 size={48} />,
      title: 'Analytics & Reports',
      description: 'Comprehensive insights into revenue, member growth, and operational metrics with detailed analytics. Make data-driven decisions.',
      details: [
        'Revenue and growth reports',
        'Member retention analytics',
        'Attendance patterns analysis',
        'Custom report generation',
        'Export to Excel/PDF'
      ],
      comingSoon: true
    },
    {
      icon: <Shield size={48} />,
      title: 'Secure & Reliable',
      description: 'Bank-level security with data encryption and regular backups to protect your gym\'s information. Your data is safe with us.',
      details: [
        'End-to-end encryption',
        'Regular automated backups',
        'GDPR compliant',
        'Role-based access control',
        'Activity logging'
      ]
    },
    {
      icon: <Smartphone size={48} />,
      title: 'Mobile App',
      description: 'Native mobile app for iOS and Android coming soon. Currently available as responsive web app that works on all devices.',
      details: [
        'Native iOS and Android apps',
        'Member self-service portal',
        'Push notifications',
        'Mobile check-in',
        'On-the-go access'
      ],
      comingSoon: true
    }
  ];

  return (
    <>
      <SEOHead
        title="Features - GymsSync | Best Gym Management Software India"
        description="Discover all features of GymsSync - member management, attendance tracking, payments, analytics and more. Complete gym ERP solution."
        keywords="gym management features, gym software features, fitness studio management, gym ERP features, gymssync features, gymsync features, gym management software features, gym software capabilities, gym management system features, fitness software features, gym software functionality, gym management tools, gym software modules, gym management features list, gym software benefits, gym management software advantages"
        ogUrl={typeof window !== 'undefined' ? window.location.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/features` : ''}
      />
      <Navbar />
      <div className="page-container">
        <div className="page-header">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-title"
          >
            Features
          </motion.h1>
          <p className="page-subtitle">
            Everything you need to manage your gym efficiently
          </p>
        </div>

        <div className="page-content">
          <div className="features-grid-detailed">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card-detailed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon-large">{feature.icon}</div>
                <h3>
                  {feature.title}
                  {feature.comingSoon && <span className="coming-soon-badge">Coming Soon</span>}
                </h3>
                <p className="feature-description">{feature.description}</p>
                <ul className="feature-details">
                  {feature.details.map((detail, dIndex) => (
                    <li key={dIndex}>{detail}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FeaturesPage;

